import { FullstoryActions } from './actions';
import { FullstoryRequestTypes, FullstoryTypes, TermsAndConditons, UserInfo } from './types';
import { combineReducers } from '@fullstory/redux-util/utils';
import { SettingsActions } from '../settings/actions';
import {
  BasePlan,
  PlanStage,
  Role,
  AppearanceMode,
  Platform,
} from '@fullstory/proto/fs/services/orgs';
import {
  AnnotationsSettings,
  FeatureFlagDefaults,
  FeatureFlags,
  GeoZone,
  OrgInfo,
  OrgLevel0,
} from '@fullstory/client-api';
import { PrivacyFlags } from '@fullstory/client-api/goimports/fs_shared_privacy';
import { SettingsTypes } from '../settings/types';
import { AnnotationsActions, AnnotationsRequestTypes } from '../annotations';

type FullstoryReducerState = {
  orgId: ReturnType<typeof orgId>;
  orgName: ReturnType<typeof orgName>;
  orgStage: ReturnType<typeof orgStage>;
  orgBasePlan: ReturnType<typeof orgBasePlan>;
  orgLevel0: ReturnType<typeof orgLevel0>;
  orgIndustry: ReturnType<typeof orgIndustry>;
  userInfo: ReturnType<typeof userInfo>;
  allOrgs: ReturnType<typeof allOrgs>;
  seatRole: ReturnType<typeof seatRole>;
  shareSessionEnabled: ReturnType<typeof shareSessionEnabled>;
  orgUseWeeklyEveryone: ReturnType<typeof orgUseWeeklyEveryone>;
  retentionTimestamp: ReturnType<typeof retentionTimestamp>;
  umbrellaId: ReturnType<typeof umbrellaId>;
  snippet: ReturnType<typeof snippet>;
  availableGeoZones: ReturnType<typeof availableGeoZones>;
  featureFlags: ReturnType<typeof featureFlags>;
  privacyFlags: ReturnType<typeof privacyFlags>;
  termsAndConditions: ReturnType<typeof termsAndConditions>;
  debug: ReturnType<typeof debug>;
  inactivityTimeout: ReturnType<typeof inactivityTimeout>;
  notificationsLastSeen: ReturnType<typeof notificationsLastSeen>;
  playbackRetentionTimestamp: ReturnType<typeof playbackRetentionTimestamp>;
  trialExpiration: ReturnType<typeof trialExpiration>;
  orgCreatedDate: ReturnType<typeof orgCreatedDate>;
  appearanceMode: ReturnType<typeof appearanceMode>;
  ssoRoleMapping: ReturnType<typeof ssoRoleMapping>;
  hasRecordedSessions: ReturnType<typeof hasRecordedSessions>;
  orgPlatform: ReturnType<typeof orgPlatform>;
  annotationsSettings: ReturnType<typeof annotationsSettings>;
};

export const fullstoryReducer = combineReducers<FullstoryReducerState>({
  orgId,
  orgName,
  orgStage,
  orgBasePlan,
  orgLevel0,
  orgIndustry,
  userInfo,
  allOrgs,
  seatRole,
  shareSessionEnabled,
  orgUseWeeklyEveryone,
  retentionTimestamp,
  umbrellaId,
  snippet,
  availableGeoZones,
  featureFlags,
  privacyFlags,
  termsAndConditions,
  debug,
  inactivityTimeout,
  notificationsLastSeen,
  playbackRetentionTimestamp,
  trialExpiration,
  orgCreatedDate,
  appearanceMode,
  ssoRoleMapping,
  hasRecordedSessions,
  orgPlatform,
  annotationsSettings,
});

/** Logged-in org ID */
function orgId(state: string = '', action: FullstoryActions): string {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.OrgId;
  }
  return state;
}

/** Logged-in org name */
function orgName(state: string = '', action: FullstoryActions): string {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.OrgName;
  }
  return state;
}

/** Logged-in org stage */
function orgStage(state: PlanStage = PlanStage.TRIAL_STAGE, action: FullstoryActions): PlanStage {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.PlanStage;
  }
  return state;
}

/** Org's base plan */
function orgBasePlan(state: BasePlan = BasePlan.UNSET_PLAN, action: FullstoryActions): BasePlan {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.BasePlan;
  }
  return state;
}

function orgLevel0(state: OrgLevel0 | null = null, action: FullstoryActions): OrgLevel0 | null {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.OrgLevel0;
    case FullstoryRequestTypes.UpdateOrgLevel0Success: {
      const { body } = action.request;

      if (body == null) throw new Error('Invariant violation: Request body must exist.');

      return {
        CompletedOnboardingVersion:
          body.completedOnboardingVersion ?? state?.CompletedOnboardingVersion ?? 0,
        CompletedPrivacyOnboarding:
          body.completedPrivacyOnboarding ?? state?.CompletedPrivacyOnboarding,
        NewFreemium: body.newFreemium ?? state?.NewFreemium,
        AcceptedTermsVersion: body.acceptedTermsVersion ?? state?.AcceptedTermsVersion ?? 0,
        SetupStage: body.setupStage ?? state?.SetupStage,
      };
    }
  }
  return state;
}

function orgIndustry(state: string = '', action: FullstoryActions): string {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.Industry ?? '';
  }
  return state;
}

function hasRecordedSessions(state: boolean = false, action: FullstoryActions): boolean {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.FirstRecordDate > 0;
  }
  return state;
}

/** Logged-in org trial expiration date */
function trialExpiration(state: number | null = null, action: FullstoryActions): number | null {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.TrialExpiration;
  }
  return state;
}

/** Org created date */
function orgCreatedDate(state: number | null = null, action: FullstoryActions): number | null {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.OrgCreated;
  }
  return state;
}

/** Logged-in user's details */
function userInfo(
  uinfo: UserInfo = { email: '', firstName: '', lastName: '' },
  action: FullstoryActions,
): UserInfo {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return {
        email: action.bootstrap.Email,
        firstName: action.bootstrap.FirstName,
        lastName: action.bootstrap.LastName,
      };
  }
  return uinfo;
}

/** All orgs for the current logged-in user */
function allOrgs(state: OrgInfo[] = [], action: FullstoryActions): OrgInfo[] {
  switch (action.type) {
    case FullstoryRequestTypes.GetAllOrgsSuccess:
      return action.data;
    case FullstoryTypes.OrgAdded:
      return [...state, action.org];
  }
  return state;
}

function seatRole(state: Role = Role.VIEW_ONLY, action: FullstoryActions): Role {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.SeatRole;
  }

  return state;
}

function orgUseWeeklyEveryone(state: boolean = false, action: FullstoryActions): boolean {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.OrgUseWeeklyEveryone;
  }
  return state;
}

function retentionTimestamp(state: number = 0, action: FullstoryActions): number {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.RetentionTimestamp;
  }
  return state;
}

function playbackRetentionTimestamp(state: number = 0, action: FullstoryActions): number {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.PlaybackRetentionTimestamp;
  }
  return state;
}

function shareSessionEnabled(
  state: boolean = false,
  action: FullstoryActions | SettingsActions,
): boolean {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.ShareSessionEnabled;
    case SettingsTypes.UpdateShareEnabledSuccess:
      return action.request.body ?? false;
  }
  return state;
}

/** Logged-in umbrella ID */
function umbrellaId(state: string = '', action: FullstoryActions): string {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.UmbrellaId;
  }
  return state;
}

/** Snippet */
function snippet(state: string = '', action: FullstoryActions): string {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.Snippet;
  }
  return state;
}

function availableGeoZones(state: GeoZone[] = [], action: FullstoryActions): GeoZone[] {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return (action.bootstrap.AvailGeoZones ?? []).filter(Boolean) as GeoZone[];
  }
  return state;
}

function featureFlags(
  state: FeatureFlags = { ...FeatureFlagDefaults },
  action: FullstoryActions,
): FeatureFlags {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.FeatureFlags ?? { ...FeatureFlagDefaults };
  }
  return state;
}

function privacyFlags(
  state: PrivacyFlags = {
    DynamicFilteredTimeRanges: '',
  },
  action: FullstoryActions,
): PrivacyFlags {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.PrivacyFlags ?? { DynamicFilteredTimeRanges: '' };
  }
  return state;
}

function termsAndConditions(
  state: TermsAndConditons = { currentVersion: 0, acceptedVersion: -1 },
  action: FullstoryActions,
): TermsAndConditons {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return {
        currentVersion: action.bootstrap.CurrentTermsVersion,
        acceptedVersion: action.bootstrap.OrgLevel0?.AcceptedTermsVersion ?? -1,
      };
    case FullstoryTypes.AcceptTermsAndConditions:
      return { ...state, acceptedVersion: state.currentVersion };
  }

  return state;
}

function debug(state: boolean = false, action: FullstoryActions): boolean {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return Boolean(action.bootstrap.Debug);
  }

  return state;
}

function inactivityTimeout(state: number = 0, action: FullstoryActions): number {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.InactivityTimeout;
  }

  return state;
}

function notificationsLastSeen(state: number = 0, action: FullstoryActions): number {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.NotificationsLastSeen;
    case FullstoryTypes.SetNotificationsLastSeen:
      return action.notificationsLastSeen;
  }

  return state;
}

function appearanceMode(
  state: AppearanceMode = AppearanceMode.APPEARANCE_MODE_LIGHT,
  action: FullstoryActions,
): AppearanceMode {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.AppearanceMode ?? state;
    case FullstoryRequestTypes.UpdateAppearanceModeRequest:
      return (action.body?.appearanceMode as AppearanceMode) ?? state;
  }

  return state;
}

function ssoRoleMapping(state: boolean = false, action: FullstoryActions): boolean {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.SsoRoleMapping ?? state;
  }

  return state;
}

function orgPlatform(state: Platform = Platform.WEB, action: FullstoryActions): Platform {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.Platform ?? state;
  }

  return state;
}

function annotationsSettings(
  state: AnnotationsSettings = { HideAnnotations: false },
  action: FullstoryActions | AnnotationsActions,
): AnnotationsSettings {
  switch (action.type) {
    case FullstoryTypes.Bootstrap:
      return action.bootstrap.AnnotationsSettings ?? state;
    case AnnotationsRequestTypes.SetAnnotationSettingsSuccess: {
      const hideAnnotations = action.request.body?.settings?.hideAnnotations;
      if (hideAnnotations === undefined) return state;

      return { ...state, HideAnnotations: hideAnnotations };
    }
  }

  return state;
}
